import { useCallback, useEffect, useRef, useState, useMemo } from "react";
import type { RealtimeChannel } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { getDeterministicColor } from "@/utils/colorHash";

export type WhiteboardUser = {
  id: string;
  name: string;
  color: string;
  cursor: { x: number; y: number } | null;
  lastSeen: number;
};

export type WhiteboardEvent = {
  type: "draw" | "add" | "modify" | "remove" | "clear" | "cursor";
  userId: string;
  payload: any;
  timestamp: number;
};

type UseWhiteboardSyncOptions = {
  meetingId: string;
  userId: string;
  userName: string;
  onRemoteEvent?: (event: WhiteboardEvent) => void;
};

const CURSOR_THROTTLE_MS = 50;

export const useWhiteboardSync = ({
  meetingId,
  userId,
  userName,
  onRemoteEvent,
}: UseWhiteboardSyncOptions) => {
  const [users, setUsers] = useState<WhiteboardUser[]>([]);
  const [isConnected, setIsConnected] = useState(false);
  const channelRef = useRef<RealtimeChannel | null>(null);
  const lastCursorBroadcast = useRef(0);
  const color = useMemo(() => getDeterministicColor(userId), [userId]);

  // Broadcast event to all participants
  const broadcastEvent = useCallback(
    (type: WhiteboardEvent["type"], payload: any) => {
      if (!channelRef.current) return;

      const event: WhiteboardEvent = {
        type,
        userId,
        payload,
        timestamp: Date.now(),
      };

      channelRef.current.send({
        type: "broadcast",
        event: "whiteboard_event",
        payload: event,
      });
    },
    [userId]
  );

  // Throttled cursor broadcast
  const broadcastCursor = useCallback(
    (x: number, y: number) => {
      const now = Date.now();
      if (now - lastCursorBroadcast.current < CURSOR_THROTTLE_MS) return;
      lastCursorBroadcast.current = now;
      broadcastEvent("cursor", { x, y });
    },
    [broadcastEvent]
  );

  // Update presence
  const updatePresence = useCallback(() => {
    if (!channelRef.current) return;

    channelRef.current.track({
      id: userId,
      name: userName,
      color,
      cursor: null,
      lastSeen: Date.now(),
    });
  }, [userId, userName, color]);

  useEffect(() => {
    if (!meetingId || !userId) return;

    const channel = supabase.channel(`whiteboard:${meetingId}`, {
      config: {
        presence: { key: userId },
        broadcast: { self: false },
      },
    });

    channelRef.current = channel;

    // Handle presence sync
    channel.on("presence", { event: "sync" }, () => {
      const state = channel.presenceState();
      const nextUsers: WhiteboardUser[] = [];

      Object.values(state).forEach((presences: any) => {
        presences.forEach((presence: any) => {
          if (!presence?.id) return;
          nextUsers.push({
            id: presence.id,
            name: presence.name,
            color: presence.color || getDeterministicColor(presence.id),
            cursor: presence.cursor,
            lastSeen: presence.lastSeen || Date.now(),
          });
        });
      });

      setUsers(nextUsers.filter((u) => u.id !== userId));
    });

    // Handle whiteboard events
    channel.on("broadcast", { event: "whiteboard_event" }, ({ payload }) => {
      if (!payload || payload.userId === userId) return;

      // Handle cursor updates separately
      if (payload.type === "cursor") {
        setUsers((prev) =>
          prev.map((u) =>
            u.id === payload.userId
              ? { ...u, cursor: payload.payload, lastSeen: Date.now() }
              : u
          )
        );
        return;
      }

      // Forward other events to handler
      onRemoteEvent?.(payload as WhiteboardEvent);
    });

    channel.subscribe((status) => {
      if (status === "SUBSCRIBED") {
        setIsConnected(true);
        updatePresence();
      } else {
        setIsConnected(false);
      }
    });

    // Heartbeat
    const heartbeat = setInterval(() => updatePresence(), 10000);

    return () => {
      clearInterval(heartbeat);
      if (channelRef.current) {
        channelRef.current.untrack?.();
        supabase.removeChannel(channelRef.current);
      }
      channelRef.current = null;
      setUsers([]);
      setIsConnected(false);
    };
  }, [meetingId, userId, userName, color, onRemoteEvent, updatePresence]);

  return {
    users,
    isConnected,
    broadcastEvent,
    broadcastCursor,
  };
};
